/* 
                        
  
  
                 
            
             
              
  
 */
import axios from 'axios';
import { BaseService } from './BaseService';
import { UrlState } from './UrlState/UrlState';
import { createSingleton } from './singleton';
import { AppConfig } from './AppConfig';
import { extractErrorMessageAndKey } from './extractErrorMessage';
import { createHeader } from './utils/request';
import Keycloak from 'keycloak-js';
import { ERROR, LOADING } from './constants';
import debug from 'debug';
import { DEFAULT_STORAGE_PROFILE, impersonatedStorageProfile, repo, setActiveStorageProfile, } from '@bi/sync';
const log = debug('core:AuthenticationService:log');
const error = debug('core:AuthenticationService:error');
const warn = debug('core:AuthenticationService:warn');
function _extractUserId(authCheckData) {
    if (authCheckData.user) {
        return authCheckData.user.id;
    }
    else {
        return parseInt(authCheckData.id);
    }
}
function _extractUserConfig(authCheckData) {
    if ('config' in authCheckData) {
        return authCheckData.config || {};
    }
    //      
    if ('user' in authCheckData) {
        return authCheckData.user.config || {};
    }
    return {};
}
function _storageProfileForAuthentication(model) {
    if (!model?.authenticated || !model.realUser)
        return DEFAULT_STORAGE_PROFILE;
    return impersonatedStorageProfile(Number(model.userId));
}
class AuthenticationModelFactory {
    static _createDefault() {
        return {
            error: null,
            loading: false,
            [ERROR]: null,
            [LOADING]: false,
            authenticating: false,
            authenticated: false,
            userId: null,
            userConfig: {},
            redirectUri: null,
        };
    }
    static withLoading() {
        const model = AuthenticationModelFactory._createDefault();
        model[LOADING] = model.loading = true;
        return model;
    }
    static withError(error, errorKey) {
        const model = AuthenticationModelFactory._createDefault();
        model.errorMessage = model.error = error || 'Unknown error';
        model.errorKey = errorKey;
        return model;
    }
    static withSignedOut() {
        const model = AuthenticationModelFactory._createDefault();
        return model;
    }
    static withSignedIn(authCheckData) {
        const impersonationList = authCheckData.impersonationList
            ?? authCheckData.realUser?.sys_config?.impersonation_list
            ?? authCheckData.sys_config?.impersonation_list;
        const mustImpersonate = authCheckData.mustImpersonate
            ?? authCheckData.realUser?.sys_config?.must_impersonate
            ?? authCheckData.sys_config?.must_impersonate;
        const model = {
            ...authCheckData,
            ...AuthenticationModelFactory._createDefault(),
            authenticated: true,
            userId: _extractUserId(authCheckData),
            userConfig: _extractUserConfig(authCheckData),
            impersonationList: Array.isArray(impersonationList) ? impersonationList : [],
            mustImpersonate: !!mustImpersonate,
        };
        return model;
    }
}
const AUTH_MODEL_FIELDS = new Set([
    'error',
    'loading',
    ERROR,
    LOADING,
    'authenticating',
    'authenticated',
    'userId',
    'userConfig',
    'isNeed2FACode',
    'isBlocked',
    'errorKey',
    'errorMessage',
    'violationMessages',
    'redirectUri',
]);
function _extractAuthCheckDataFromModel(model) {
    if (!model?.authenticated)
        return null;
    if (!('user' in model) && !('id' in model))
        return null;
    const authCheckData = {};
    for (const key of Object.keys(model)) {
        if (!AUTH_MODEL_FIELDS.has(key))
            authCheckData[key] = model[key];
    }
    return authCheckData;
}
function _isReactNativeRuntime() {
    return typeof navigator !== 'undefined' && navigator.product === 'ReactNative';
}
function _isHttpErrorIsNotAuthenticated(error) {
    if (!error.isAxiosError)
        return false;
    if (!error.response)
        return false; //              
    if (error.response.status === 401) {
        //                                             
        return true;
    }
    if (error.response.status === 403) {
        const response = error.response;
        try {
            const { key, type, message } = response.data;
            if (key === 'INVALID_SESSION' || key === 'WRONG_PASSWORD_OR_USER_NAME' || key === 'SESSION_NOT_FOUND') {
                return true;
            }
        }
        catch (err) {
            //              
        }
        //                                                      
        if (response.data === null) {
            //                 
            return true;
        }
        //                                                   
        if (typeof response.data === 'string' && (response.data.startsWith('<!doctype html>') || response.data.startsWith('<html'))) {
            return true;
        }
    }
    return false;
}
export class AuthenticationService extends BaseService {
    _keycloak;
    _authRepository = null;
    constructor() {
        super(AuthenticationModelFactory.withLoading());
        axios.interceptors.request.use(request => {
            const biAppIdent = AppConfig.getModel()?.biAppIdent;
            const customHeaders = createHeader(request.headers || {}, { biAppIdent });
            for (const header in customHeaders) {
                request.headers.set(header, customHeaders[header]);
            }
            return request;
        }, error => {
            Promise.reject(error);
        });
        axios.interceptors.response.use(response => response, error => {
            const externalUrl = (error?.config?.url ?? '').startsWith('http://') || (error?.config?.url ?? '').startsWith('https://'); //                              
            if (!externalUrl && _isHttpErrorIsNotAuthenticated(error)) { //                                  
                this._notifyLoggedOut();
            }
            return Promise.reject(error);
        });
        this._init();
    }
    _getAuthRepository() {
        if (!AuthenticationService.isLocalAuthSessionEnabled())
            return null;
        if (!this._authRepository) {
            this._authRepository = new repo.AuthRepository(() => this.check());
        }
        return this._authRepository;
    }
    //                                                                          
    //                                                 
    async _init() {
        try {
            const authRepository = this._getAuthRepository();
            const authCheckData = await (authRepository ? authRepository.check() : this.check());
            if (authCheckData) {
                this.acceptAuthCheckData(authCheckData);
            }
            else {
                this._setModel(AuthenticationModelFactory.withSignedOut());
            }
        }
        catch (err) {
            let errorResponse = null, httpStatus = err?.response?.status, errorKey, errorMessage;
            try {
                errorResponse = err.response.data;
            }
            catch (err2) {
                /* */
            }
            if (errorResponse && errorResponse.authRedirect) {
                return;
            }
            if (errorResponse) {
                errorKey = errorResponse.key;
                errorMessage = errorResponse.message;
            }
            //                         
            if (httpStatus === 303 && errorResponse?.redirect_uri) {
                let redirectUri = errorResponse.redirect_uri;
                try {
                    //                                                              
                    const u = new URL(redirectUri); //                                                  
                    const luxmsbi_target = window.location.pathname + window.location.hash;
                    const shouldRedirect = u.searchParams.get('redirect_uri') && luxmsbi_target && luxmsbi_target !== '/';
                    if (shouldRedirect) {
                        const uu = new URL(u.searchParams.get('redirect_uri'));
                        uu.searchParams.set('luxmsbi_target', btoa(luxmsbi_target));
                        u.searchParams.set('redirect_uri', uu.toString());
                        redirectUri = u.toString();
                    }
                }
                catch (err) {
                    //     
                }
                if (!AppConfig.hasFeature('!OIDCRedirect')) {
                    window.location.assign(redirectUri);
                }
                else {
                    this._updateModel({
                        error: null,
                        loading: false,
                        [ERROR]: null,
                        [LOADING]: false,
                        errorKey: null,
                        redirectUri,
                    });
                }
                return;
            }
            if (errorResponse && errorResponse.key === 'LOGIN_COUNT_LIMIT_EXCEEDED') {
                this._updateModel({
                    error: errorResponse.message,
                    loading: false,
                    [ERROR]: errorResponse.message,
                    [LOADING]: false,
                    redirectUri: null,
                    isBlocked: true,
                    errorKey,
                    errorMessage,
                });
                return;
            }
            const extractedError = extractErrorMessageAndKey(err);
            this._setModel(AuthenticationModelFactory.withError(extractedError.message, extractedError.key));
        }
    }
    _setModel(m, opts = {}) {
        const prevModel = this._model;
        const prevAuthenticated = prevModel ? prevModel.authenticated : false;
        //                                                                      
        //                                                           
        setActiveStorageProfile(_storageProfileForAuthentication(m));
        super._setModel(m);
        if (m.authenticated) {
            const authCheckData = opts.authCheckData ?? (!prevAuthenticated ? _extractAuthCheckDataFromModel(m) : null);
            if (authCheckData) {
                if (!opts.authCheckData) {
                    console.warn('[AuthenticationService] Deprecated direct _setModel(authenticated) call detected. ' +
                        'Use AuthenticationService.acceptAuthCheckData(authCheckData) instead.');
                }
                this._getAuthRepository()?.saveSuccessfulAuth(authCheckData).catch(err => {
                    warn('[AuthenticationService] failed to persist auth session', err);
                });
            }
        }
        //                                             
        if (prevAuthenticated !== m.authenticated) {
            if (m.authenticated) {
                this._notify('sign-in', this._model);
            }
            else {
                this._notify('sign-out', this._model);
            }
        }
    }
    isAuthenticated() {
        return this._model.authenticated;
    }
    acceptLocalImpersonationTarget(target) {
        if (!this._model.authenticated) {
            throw new Error('Cannot select an impersonation target before authentication');
        }
        const userId = Number(target.id);
        if (!Number.isInteger(userId)) {
            throw new Error(`Invalid impersonation target id: ${target.id}`);
        }
        const currentAuthData = _extractAuthCheckDataFromModel(this._model);
        if (!currentAuthData) {
            throw new Error('Current authentication model cannot be used for local impersonation');
        }
        const currentModel = this._model;
        const model = {
            ...currentModel,
            id: String(userId),
            userId,
            username: target.username,
            name: target.name,
            realUser: currentModel.realUser ?? currentAuthData,
        };
        if (currentModel.user) {
            model.user = {
                ...currentModel.user,
                id: userId,
                username: target.username,
                name: target.name,
            };
        }
        //                                                                 
        //                                                                            
        this._setModel(model);
        return this._model;
    }
    acceptAuthCheckData(authCheckData) {
        const model = AuthenticationModelFactory.withSignedIn(authCheckData);
        this._setModel(model, { authCheckData });
        return this._model;
    }
    //                                                                          
    //                                                 
    async _check() {
        const urlState = UrlState.getModel();
        const appConfig = AppConfig.getModel();
        const keycloakSets = appConfig.keycloak;
        const getAuthToken = appConfig.getAuthToken;
        if (AuthenticationService.FORCE_LOGIN_KEY in urlState) {
            return null;
        }
        let customToken = null; //                                          
        const installInterceptor = () => {
            axios.interceptors.request.use(request => {
                const token = customToken || this._keycloak.token;
                request.headers.set('Authorization', `Bearer ${token}`);
                return request;
            }, error => {
                Promise.reject(error);
            });
        };
        if (typeof getAuthToken === 'function') {
            const requestToken = async () => {
                let result = getAuthToken();
                if (result instanceof Promise)
                    result = await result;
                if (typeof result === 'string') {
                    customToken = result;
                    return true;
                }
                else if (typeof result === 'object' && result && result.token) {
                    customToken = result.token;
                    if (result.expiresIn) {
                        const timeout = Math.max(result.expiresIn - new Date().valueOf() - 10, 10);
                        window.setTimeout(requestToken, timeout);
                    }
                    return true;
                }
                //                                                                 
                return false;
            };
            try {
                if (await requestToken()) {
                    installInterceptor();
                }
            }
            catch (err) {
                error('[AuthenticationService] [check]: getAuthToken error occured', err);
            }
        }
        else if (keycloakSets) {
            this._keycloak = new Keycloak(keycloakSets);
            await this._keycloak
                .init({
                onLoad: 'login-required',
                flow: 'standard',
            })
                .then(authenticated => {
                if (this._keycloak.token) {
                    if (typeof window !== 'undefined')
                        window.keycloak = this._keycloak;
                    installInterceptor();
                }
            })
                .catch(err => {
                error('[AuthenticationService] [check]: Keycloak error occured: ', err.message);
            });
            this._keycloak.onTokenExpired = () => {
                this._keycloak
                    .updateToken(10)
                    .then(() => log('[AuthenticationService] [check]: Token successfully updated!'))
                    .catch(err => {
                    error('[AuthenticationService] [check]: Keycloak error occured:', err);
                });
            };
        }
        const noSso = AuthenticationService.NO_SSO_KEY in urlState;
        const url = AppConfig.fixRequestUrl(noSso ? '/api/auth/check-no-sso' : '/api/auth/check');
        const data = (await axios.get(url)).data;
        return data;
    }
    async check() {
        try {
            const data = await this._check();
            return data;
        }
        catch (err) {
            let errorResponse = null;
            errorResponse = err?.response?.data;
            if (errorResponse && errorResponse.authRedirect) {
                log('Auth with redirect', errorResponse.authRedirect);
                window.location.href = errorResponse.authRedirect;
                throw err;
            }
            if (errorResponse && errorResponse.key === 'LOGIN_COUNT_LIMIT_EXCEEDED') {
                throw err;
            }
            if (_isHttpErrorIsNotAuthenticated(err)) {
                return null;
            }
            throw err;
        }
    }
    async _clearLocalSession() {
        try {
            await this._getAuthRepository()?.clearLocalSession();
        }
        catch (err) {
            warn('[AuthenticationService] failed to clear auth session', err);
        }
    }
    _notifyLoggedOut() {
        void this._clearLocalSession();
        this._setModel(AuthenticationModelFactory.withSignedOut());
    }
    async signIn(username, password, new_password) {
        if (this._model.authenticated) {
            warn('[AuthenticationService] [sign-in] already authenticated');
        }
        if (this._model.authenticating) {
            warn('[AuthenticationService] [sign-in] already authenticating');
        }
        try {
            //                                                          
            this._updateModel({ authenticating: true });
            const url = AppConfig.fixRequestUrl(`/api/auth/login`);
            let postData = {
                username: username,
                password: password,
            };
            if (new_password) {
                postData = {
                    ...postData,
                    new_password
                };
            }
            const result = await axios({
                method: 'post',
                url: url,
                data: postData,
                headers: { 'Content-Type': 'application/json; charset=UTF-8' },
            });
            let data = result.data;
            if (data && data.authType === '2fa') {
                const nonCompleteModel = AuthenticationModelFactory.withSignedIn(data);
                nonCompleteModel.authenticated = false;
                nonCompleteModel.isNeed2FACode = true;
                this._setModel(nonCompleteModel);
                return this._model;
            }
            if (!(data && ('user' in data || 'id' in data))) {
                warn('[AuthenticationService] Login returned null');
                //                                             
                //                             
                data = await this._check();
                if (!data) {
                    this._setModel(AuthenticationModelFactory.withSignedOut());
                    return this._model;
                }
            }
            //        
            const FORCE_LOGIN_KEY = AuthenticationService.FORCE_LOGIN_KEY;
            if (FORCE_LOGIN_KEY in UrlState.getModel()) {
                UrlState.navigate({ [FORCE_LOGIN_KEY]: null });
            }
            this.acceptAuthCheckData(data);
            return this._model;
        }
        catch (err) {
            if (_isHttpErrorIsNotAuthenticated(err)) {
                this._setModel(AuthenticationModelFactory.withSignedOut());
                return this._model;
            }
            //              
            try {
                const errorResponse = err.response.data;
                if (errorResponse.authType === '2fa') {
                    this._setModel({
                        error: null,
                        loading: false,
                        [ERROR]: null,
                        [LOADING]: false,
                        authenticated: false,
                        authenticating: false, //            
                        isNeed2FACode: true,
                    });
                    return this._model;
                }
            }
            catch (xErr) {
                //
            }
            const extractedError = extractErrorMessageAndKey(err);
            //                                                                 
            //                                                      
            if (extractedError.key) {
                this._setModel({
                    error: null,
                    loading: false,
                    [ERROR]: null,
                    [LOADING]: false,
                    authenticated: false,
                    authenticating: false,
                    errorKey: extractedError.key,
                    errorMessage: extractedError.message,
                    violationMessages: err.response?.data?.messages,
                });
                return this._model;
            }
            this._setModel(AuthenticationModelFactory.withError(extractedError.message, extractedError.key));
            //           
            return this._model;
        }
    }
    async signOut() {
        await this._clearLocalSession();
        const url = AppConfig.fixRequestUrl(`/api/auth/logout`);
        let offline = false;
        try {
            await axios.get(url);
        }
        catch (err) {
            if (err.isAxiosError && err.response?.status === 303 && err.response?.data?.redirect_uri) {
                let redirectUri = err.response.data.redirect_uri;
                window.location?.assign?.(redirectUri);
                return;
            }
            //                                                                   
            //                                                                
            if (!err.isAxiosError || err.response)
                throw err;
            offline = true;
        }
        if (this._keycloak && !offline) {
            await this._keycloak.logout();
        }
        this._notifyLoggedOut();
        if (typeof window !== 'undefined')
            window.location?.reload?.(); //                  
        return this._model;
    }
    async resendCode() {
        try {
            const url = AppConfig.fixRequestUrl(`/api/auth/2fa/resend-factor2`);
            const result = (await axios.get(url)).data;
            log('resend code', result);
        }
        catch (err) {
            //                      
            throw err;
        }
    }
    async signInWithCode(code) {
        try {
            const url = AppConfig.fixRequestUrl('/api/auth/2fa/login2');
            const data = (await axios.post(url, { factor2: parseInt(code, 10) })).data;
            //             
            this.acceptAuthCheckData(data);
        }
        catch (err) {
            error('Error occured while signing in with code');
            throw err;
        }
    }
    static NOT_AUTHENTICATED = 'Not authenticated';
    static FORCE_LOGIN_KEY = 'loginme';
    static NO_SSO_KEY = 'no-sso';
    static getInstance = createSingleton(() => new AuthenticationService(), '__authenticationService');
    //
    //                            
    //
    static getModel() {
        return this.getInstance().getModel();
    }
    static subscribeUpdatesAndNotify(listener) {
        return this.getInstance().subscribeUpdatesAndNotify(listener);
    }
    static subscribeUpdates(listener) {
        return this.getInstance().subscribeUpdates(listener);
    }
    static unsubscribe(listener) {
        return this.getInstance().unsubscribe(listener);
    }
    static signOut() {
        return this.getInstance().signOut();
    }
    static signInWithCode(code) {
        return this.getInstance().signInWithCode(code);
    }
    static async signIn(username, password) {
        return this.getInstance().signIn(username, password);
    }
    static resendCode() {
        return this.getInstance().resendCode();
    }
    static acceptAuthCheckData(authCheckData) {
        return this.getInstance().acceptAuthCheckData(authCheckData);
    }
    static isLocalAuthSessionEnabled() {
        const appConfig = AppConfig.getModel();
        return !!appConfig?.authentication?.offlineSession || _isReactNativeRuntime();
    }
}
